import React, { useState, useEffect } from 'react'
import {
  Key,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Shield,
  Clock,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface APIKey {
  id: number
  name: string
  key: string
  permissions: string[]
  is_active: boolean
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

const AVAILABLE_PERMISSIONS = [
  { value: 'leads:read', label: 'Leads — Leitura', description: 'Listar e visualizar leads com negociações, tags e conversas' },
  { value: 'admin', label: 'Administrador', description: 'Acesso total à API externa' },
]

const APIKeys: React.FC = () => {
  const navigate = useNavigate()
  const [keys, setKeys] = useState<APIKey[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null)
  const [newKey, setNewKey] = useState<string | null>(null) // chave recém-criada (exibida uma vez)
  const [copiedId, setCopiedId] = useState<number | 'new' | null>(null)
  const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    permissions: ['leads:read'] as string[],
    expires_at: '',
  })

  useEffect(() => {
    loadKeys()
  }, [])

  const loadKeys = async () => {
    setLoading(true)
    try {
      const res = await api.get('/integrations/api-keys/')
      const data = Array.isArray(res.data) ? res.data : (res.data.results || [])
      setKeys(data)
    } catch {
      setError('Erro ao carregar chaves de API.')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Informe um nome para a chave.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload: Record<string, any> = {
        name: formData.name,
        permissions: formData.permissions,
        is_active: true,
      }
      if (formData.expires_at) payload.expires_at = formData.expires_at
      const res = await api.post('/integrations/api-keys/', payload)
      setNewKey(res.data.key)
      setShowCreateModal(false)
      setFormData({ name: '', permissions: ['leads:read'], expires_at: '' })
      loadKeys()
    } catch {
      setError('Erro ao criar chave.')
    } finally {
      setSaving(false)
    }
  }

  const handleRegenerate = async (key: APIKey) => {
    if (!confirm(`Regenerar a chave "${key.name}"? A chave atual deixará de funcionar imediatamente.`)) return
    try {
      const res = await api.post(`/integrations/api-keys/${key.id}/regenerate/`)
      setNewKey(res.data.key)
      loadKeys()
    } catch {
      setError('Erro ao regenerar chave.')
    }
  }

  const handleToggleActive = async (key: APIKey) => {
    try {
      await api.patch(`/integrations/api-keys/${key.id}/`, { is_active: !key.is_active })
      loadKeys()
    } catch {
      setError('Erro ao atualizar chave.')
    }
  }

  const handleDelete = async () => {
    if (!selectedKey) return
    try {
      await api.delete(`/integrations/api-keys/${selectedKey.id}/`)
      setShowDeleteModal(false)
      setSelectedKey(null)
      loadKeys()
    } catch {
      setError('Erro ao deletar chave.')
    }
  }

  const handleCopy = (text: string, id: number | 'new') => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const toggleVisibility = (id: number) => {
    setVisibleKeys(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const maskKey = (key: string) => key.slice(0, 10) + '••••••••••••••••••••••••'

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  // ── Styles ──────────────────────────────────────────────────────────────────

  const glass: React.CSSProperties = {
    background: 'var(--glass-bg-strong)',
    backdropFilter: 'var(--glass-blur)',
    WebkitBackdropFilter: 'var(--glass-blur)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-xl)',
  }

  const btnPrimary: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
    padding: 'var(--space-2-5) var(--space-4)',
    background: 'var(--bg-gradient)', color: 'white',
    border: 'none', borderRadius: 'var(--radius-lg)',
    cursor: 'pointer', fontWeight: 600, fontSize: 'var(--text-sm)',
  }

  const btnSecondary: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
    padding: 'var(--space-2-5) var(--space-4)',
    background: 'var(--glass-bg)', color: 'var(--text-primary)',
    border: '1px solid var(--glass-border-strong)', borderRadius: 'var(--radius-lg)',
    cursor: 'pointer', fontWeight: 500, fontSize: 'var(--text-sm)',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: 'var(--space-3) var(--space-4)',
    background: 'var(--glass-bg)', border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-xl)', fontSize: 'var(--text-sm)',
    color: 'var(--text-primary)', outline: 'none',
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: '900px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <button onClick={() => navigate('/settings')} style={{ ...btnSecondary, padding: 'var(--space-2)' }}>
          <ArrowLeft size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--text-primary)' }}>
            Chaves de API
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Gerencie as chaves para integrar sistemas externos à sua conta.
          </p>
        </div>
        <button onClick={() => { setShowCreateModal(true); setError('') }} style={btnPrimary}>
          <Plus size={16} /> Nova Chave
        </button>
      </div>

      {/* Alerta de nova chave criada */}
      {newKey && (
        <div style={{
          ...glass, padding: 'var(--space-4)', marginBottom: 'var(--space-5)',
          borderColor: 'rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
            <AlertTriangle size={16} color="var(--color-success-600)" />
            <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)', color: 'var(--color-success-700)' }}>
              Copie sua chave agora — ela não será exibida novamente!
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <code style={{
              flex: 1, padding: 'var(--space-2) var(--space-3)',
              background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', fontFamily: 'monospace', color: 'var(--text-primary)',
              wordBreak: 'break-all',
            }}>
              {newKey}
            </code>
            <button onClick={() => handleCopy(newKey, 'new')} style={{ ...btnSecondary, flexShrink: 0 }}>
              {copiedId === 'new' ? <Check size={15} color="var(--color-success-600)" /> : <Copy size={15} />}
              {copiedId === 'new' ? 'Copiado!' : 'Copiar'}
            </button>
            <button onClick={() => setNewKey(null)} style={{ ...btnSecondary, padding: 'var(--space-2)', flexShrink: 0 }}>
              ✕
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'rgba(239,68,68,0.1)', color: 'var(--color-danger-600)', fontSize: 'var(--text-sm)' }}>
          {error}
        </div>
      )}

      {/* Lista de chaves */}
      <div style={{ ...glass, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Carregando...
          </div>
        ) : keys.length === 0 ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center' }}>
            <Key size={40} color="var(--text-tertiary)" style={{ marginBottom: 'var(--space-3)' }} />
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Nenhuma chave criada ainda.</p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)', margin: '4px 0 0' }}>
              Crie uma chave para integrar sistemas externos via HTTP.
            </p>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 220px 110px 110px 130px',
              gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-5)',
              background: 'var(--bg-tertiary)', fontSize: 'var(--text-xs)',
              fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              <div>Nome / Chave</div>
              <div>Permissões</div>
              <div>Última uso</div>
              <div>Expira em</div>
              <div style={{ textAlign: 'right' }}>Ações</div>
            </div>

            {keys.map((key, idx) => (
              <div key={key.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 220px 110px 110px 130px',
                gap: 'var(--space-3)', padding: 'var(--space-4) var(--space-5)',
                alignItems: 'center', fontSize: 'var(--text-sm)',
                borderTop: idx > 0 ? '1px solid var(--border-light)' : 'none',
                opacity: key.is_active ? 1 : 0.5,
              }}>
                {/* Nome + chave */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{key.name}</span>
                    {!key.is_active && (
                      <span style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'rgba(156,163,175,0.2)', color: 'var(--text-tertiary)' }}>
                        Inativa
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                    <code style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                      {visibleKeys.has(key.id) ? key.key : maskKey(key.key)}
                    </code>
                    <button onClick={() => toggleVisibility(key.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-tertiary)' }}>
                      {visibleKeys.has(key.id) ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                    <button onClick={() => handleCopy(key.key, key.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-tertiary)' }}>
                      {copiedId === key.id ? <Check size={12} color="var(--color-success-600)" /> : <Copy size={12} />}
                    </button>
                  </div>
                </div>

                {/* Permissões */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                  {(key.permissions || []).map(p => (
                    <span key={p} style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '2px 8px', borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--text-xs)', fontWeight: 600,
                      background: 'rgba(99,102,241,0.1)', color: 'var(--primary-600)',
                    }}>
                      <Shield size={10} /> {p}
                    </span>
                  ))}
                </div>

                {/* Último uso */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                  <Clock size={12} />
                  {formatDate(key.last_used_at)}
                </div>

                {/* Expira em */}
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                  {formatDate(key.expires_at)}
                </div>

                {/* Ações */}
                <div style={{ display: 'flex', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => handleToggleActive(key)}
                    title={key.is_active ? 'Desativar' : 'Ativar'}
                    style={{ background: 'none', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '6px', cursor: 'pointer', color: key.is_active ? 'var(--color-success-600)' : 'var(--text-tertiary)' }}
                  >
                    <Shield size={14} />
                  </button>
                  <button
                    onClick={() => handleRegenerate(key)}
                    title="Regenerar chave"
                    style={{ background: 'none', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '6px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                  >
                    <RefreshCw size={14} />
                  </button>
                  <button
                    onClick={() => { setSelectedKey(key); setShowDeleteModal(true) }}
                    title="Excluir"
                    style={{ background: 'none', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '6px', cursor: 'pointer', color: 'var(--color-danger-600)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Info de uso */}
      <div style={{ ...glass, padding: 'var(--space-4)', marginTop: 'var(--space-5)' }}>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text-primary)' }}>
          Como usar
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Listar leads (com negociações, tags e conversas):</p>
            <code style={{ display: 'block', padding: 'var(--space-2) var(--space-3)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', fontFamily: 'monospace', color: 'var(--text-primary)' }}>
              GET {window.location.origin.replace('3000', '8000')}/api/external/v1/leads/{'  '}X-API-Key: flpr_xxx
            </code>
          </div>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Buscar lead específico:</p>
            <code style={{ display: 'block', padding: 'var(--space-2) var(--space-3)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', fontFamily: 'monospace', color: 'var(--text-primary)' }}>
              GET /api/external/v1/leads/{'{id}'}{'  '}X-API-Key: flpr_xxx
            </code>
          </div>
        </div>
      </div>

      {/* Modal: Criar chave */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-4)' }}>
          <div style={{ ...glass, padding: 'var(--space-6)', width: '100%', maxWidth: '480px' }}>
            <h3 style={{ margin: '0 0 var(--space-5)', fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--text-primary)' }}>
              Nova Chave de API
            </h3>

            {error && <div style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'rgba(239,68,68,0.1)', color: 'var(--color-danger-600)', fontSize: 'var(--text-sm)' }}>{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Nome *
                </label>
                <input
                  style={inputStyle}
                  placeholder="Ex: n8n Integration, Zapier..."
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Permissões
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {AVAILABLE_PERMISSIONS.map(perm => (
                    <label key={perm.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', cursor: 'pointer', padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)', border: `1px solid ${formData.permissions.includes(perm.value) ? 'var(--primary-400)' : 'var(--border-light)'}`, background: formData.permissions.includes(perm.value) ? 'rgba(99,102,241,0.06)' : 'transparent' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(perm.value)}
                        onChange={e => setFormData(p => ({
                          ...p,
                          permissions: e.target.checked
                            ? [...p.permissions, perm.value]
                            : p.permissions.filter(x => x !== perm.value)
                        }))}
                        style={{ marginTop: '2px', flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>{perm.label}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: '2px' }}>{perm.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 'var(--space-1)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Expiração (opcional)
                </label>
                <input
                  type="date"
                  style={inputStyle}
                  value={formData.expires_at}
                  onChange={e => setFormData(p => ({ ...p, expires_at: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
              <button onClick={() => { setShowCreateModal(false); setError('') }} style={btnSecondary}>
                Cancelar
              </button>
              <button onClick={handleCreate} disabled={saving} style={btnPrimary}>
                {saving ? 'Gerando...' : <><Key size={15} /> Gerar Chave</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Confirmar exclusão */}
      {showDeleteModal && selectedKey && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--space-4)' }}>
          <div style={{ ...glass, padding: 'var(--space-6)', width: '100%', maxWidth: '420px' }}>
            <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--text-primary)' }}>
              Excluir chave?
            </h3>
            <p style={{ margin: '0 0 var(--space-5)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              A chave <strong>"{selectedKey.name}"</strong> será excluída permanentemente e todas as integrações que a utilizam pararão de funcionar.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteModal(false)} style={btnSecondary}>Cancelar</button>
              <button onClick={handleDelete} style={{ ...btnPrimary, background: 'var(--color-danger-600)' }}>
                <Trash2 size={15} /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default APIKeys
